import { useSignals } from '@preact/signals-react/runtime';
import { Row } from 'antd';
import Icon from 'assets/Icons';
import ModalConfirm, { useModalConfirmRef } from 'components/Modal/ModalConfirm';
import Text from 'components/Text';
import appointmentActions from 'features/appointment/services/actions';
import { BLOCK_HOURS_CONFIG } from 'features/appointment/services/constants';
import { ICalendarHeader, ICalendarItemData } from 'features/appointment/services/types/calendar';
import { ILockBreakTimeItem } from 'features/appointment/services/types/lockBreakTime';
import { FORMAT_TIME } from 'features/appointment/utils/format';
import { first, get, maxBy, minBy } from 'lodash';
import React, { useMemo } from 'react';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { momentTimezone } from 'utils/time';
type IBlockHourProps = {
  data: ICalendarItemData[];
  isMonthView?: boolean;
  dateOfMonth?: string;
  popup?: boolean;
  headerData?: ICalendarHeader;
};
const BlockHour: React.FC<IBlockHourProps> = ({ data = [], isMonthView, dateOfMonth, popup, headerData }) => {
  useSignals();
  const modalConfirm = useModalConfirmRef();
  const dispatch = useAppDispatch();
  const startTime = useMemo(() => {
    const item = minBy(data, o => momentTimezone(o.startTime).valueOf());
    if (!item?.startTime) return '';
    return momentTimezone(item?.startTime).format(FORMAT_TIME);
  }, [data]);

  const endTime = useMemo(() => {
    const item = maxBy(data, o => momentTimezone(o.endTime).valueOf());
    if (!item?.endTime) return '';
    return momentTimezone(item?.endTime).format(FORMAT_TIME);
  }, [data]);

  const addMoreTime: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    dispatch(appointmentActions.addMoreBreakTime(get(data, [0, 'id'], '')));
  };
  const minusTime: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    dispatch(appointmentActions.minusBreakTime(get(data, [0, 'id'], '')));
  };

  const extraData = useMemo(() => first(data)?.data as ILockBreakTimeItem, [data]);

  const remove = () => {
    const _data = first(data);
    if (!_data?.id) return;
    modalConfirm.current?.open('Do you like to remove this items?', () => dispatch(appointmentActions.removeBreakBlockHour(_data.id)));
  };

  return (
    <>
      <BlockHourStyled popup={popup}>
        {!popup && <div className="overlay"></div>}
        <div className='box-content'>
          <div>
            {dateOfMonth && <Text variant='BODY_1' color='text_2' mb={1} p={0.25} textAlign='left'>{dateOfMonth}</Text>}
            <Text variant='H7' style={{ fontWeight: '600' }} color='text_3'>Block Times</Text>
            <Text variant='CAPTION_3' color='text_3' style={{ fontSize: '1rem' }}>{startTime} - {endTime}</Text>
          </div>
          {extraData?.reason && <Row align={'middle'}>
            <Text variant='CAPTION_2' color='text_3' mr={0.25}>{'Reason: '}</Text>
            <Text variant='CAPTION_3' color='text_3' style={{ textAlign: 'left' }}>{extraData?.reason}</Text>
          </Row>}
        </div>
        {!headerData?.modal && <>
          <ButtonRemove onClick={remove}>
            <div style={{ width: '45px', height: '45px' }}><Icon type='closeModal' /></div>
          </ButtonRemove>
          {!isMonthView && extraData.type !== 'OFF' && (
            <>
              <Row className='bottom' align={'middle'} justify={'center'} style={{ gap: '1rem' }}>
                <Row align={'middle'} justify={'center'}>
                  <button onClick={minusTime}>
                    <Icon type='minusAppointment' />
                  </button>
                </Row>
                <Row align={'middle'} justify={'center'}>
                  <button onClick={addMoreTime}>
                    <Icon type='addAppointment' />
                  </button>
                </Row>
              </Row>
            </>
          )}
        </>}
      </BlockHourStyled>
      <ModalConfirm ref={modalConfirm} />
    </>
  );
};

export default React.memo(BlockHour);
type BlockHourStyledProps = {
  popup?: boolean;
}
const BlockHourStyled = styled.div<BlockHourStyledProps>`
  height: 100%;
  width: 100%;
  display: flex;
  position: relative; 
  .box-content {
    border-radius: 0px;

    display: flex;
    flex-direction: column;
    padding: 4px 8px;
    flex: 1;
    .flex-grow {
      flex:1;
    }
  }
  .bottom {
    position: absolute;
    bottom:0;
    width:100%;
    transform: translateY(12px);
    button {
      border-radius: 100%;
    }
  }

  ${({ popup }) => !popup && `
  .overlay {
    position: absolute;
    inset: 0;
    background: rgba(229,229,229);
    opacity: 0;
  }
  .box-content {
    background: ${BLOCK_HOURS_CONFIG.value.fill};
    border: 1.5px solid ${BLOCK_HOURS_CONFIG.value.stroke};
  }
  `}
`;

const ButtonRemove = styled.button`
  position: absolute;
  right: 4px;
  top: 4px;
`;
